import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faSave } from "@fortawesome/pro-solid-svg-icons/faSave";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tag from "antd/es/tag";
import cn from "classnames";
import isEmpty from "lodash-es/isEmpty";
import { useIntl } from "react-intl";

import { EntityType, EntityTypesSupportingSavedFilters } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import SavedFilter, {
  MapSavedFilter,
  RegularSavedFilter,
} from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { Button } from "@mapmycustomers/ui";

import useView from "@app/component/view/utils/useView";
import { RootState } from "@app/store/rootReducer";
import { isSavedFilterUpdateLoading } from "@app/store/savedFilters";
import { updateSavedFilter } from "@app/store/savedFilters/actions";
import convertFilterModelToMapFilterModel from "@app/store/savedFilters/convertFilterModelToMapFilterModel";
import convertMapFilterModelToFilterModel from "@app/store/savedFilters/convertMapFilterModelToFilterModel";
import layout from "@app/styles/layout";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useClearFilters from "@app/util/hook/useClearFilters";

import ActionPopconfirm from "./ActionPopconfirm";
import styles from "./CommonStyles.module.scss";
import getFilterDifferencesCount from "./util/getFilterDifferencesCount";

interface Props {
  entityType: Exclude<EntityTypesSupportingSavedFilters, EntityType.PIN>;
  implicitFilterFields?: IField[];
  isUpdateLoading: boolean;
  onCreate: () => void;
  onDelete: (savedFilter: SavedFilter) => void;
  onUpdateSavedFilter: (filter?: SavedFilter) => void;
  savedFilter: SavedFilter;
  updateSavedFilter: typeof updateSavedFilter.request;
}

const SelectedFilterState: React.FC<Props> = ({
  entityType,
  implicitFilterFields,
  isUpdateLoading,
  onCreate,
  onDelete,
  onUpdateSavedFilter,
  savedFilter,
  updateSavedFilter,
}) => {
  const analyticIssuer = useAnalytics();
  const intl = useIntl();
  const { updateViewAs, viewState } = useView();
  const handleResetAllFilters = useClearFilters(implicitFilterFields);
  const { columns, filter, viewAs } = viewState;
  const isFilterEmpty = useMemo(() => isEmpty(filter), [filter]);

  const handleClearSelectedFilter = useCallback(() => {
    handleResetAllFilters();
    updateViewAs(undefined);
    onUpdateSavedFilter(undefined);
  }, [handleResetAllFilters, onUpdateSavedFilter, updateViewAs]);

  const handleUpdateExistsFilter = useCallback(() => {
    analyticIssuer.completed(["Update Filter"], { filters: filter });
    updateSavedFilter({
      callback: (savedFilter) => onUpdateSavedFilter(savedFilter as SavedFilter),
      columns,
      filter: {
        ...savedFilter,
        filters:
          entityType === EntityType.ACTIVITY
            ? filter
            : convertFilterModelToMapFilterModel(entityType, filter),
        viewAs,
      },
    });
  }, [
    analyticIssuer,
    columns,
    entityType,
    filter,
    onUpdateSavedFilter,
    savedFilter,
    updateSavedFilter,
    viewAs,
  ]);

  const handleCreateFilter = useCallback(() => {
    analyticIssuer.using(["Save as New"], { filter });
    onCreate();
  }, [analyticIssuer, filter, onCreate]);

  const differencesCount: number = useMemo(
    () =>
      getFilterDifferencesCount(
        filter,
        entityType === EntityType.ACTIVITY
          ? (savedFilter as RegularSavedFilter).filters
          : convertMapFilterModelToFilterModel(entityType, (savedFilter as MapSavedFilter).filters),
        viewAs,
        savedFilter.viewAs,
        implicitFilterFields
      ),
    [entityType, filter, implicitFilterFields, savedFilter, viewAs]
  );

  const handleDeleteFilter = useCallback(() => {
    onDelete(savedFilter);
    handleClearSelectedFilter();
  }, [handleClearSelectedFilter, onDelete, savedFilter]);

  return (
    <Row className={cn(styles.container, { [styles.hasModifications]: differencesCount > 0 })}>
      <Col className={styles.filtersCount}>
        <Row align="middle" gutter={layout.spacerM}>
          <Col>
            <div className={styles.selectedFilterIconContainer}>
              <FontAwesomeIcon className={styles.selectedFilterIcon} icon={faSave} size="xs" />
            </div>
          </Col>
          <Col>
            <span>{savedFilter.name}</span>
          </Col>
          {differencesCount > 0 && (
            <Col>
              <Tag color="processing">
                {intl.formatMessage(
                  {
                    id: "savedFilters.status.changesCount",
                    defaultMessage: "{count} {count, plural, one {Change} other {Changes}}",
                    description: "Saved filters changes count",
                  },
                  { count: differencesCount }
                )}
              </Tag>
            </Col>
          )}
        </Row>
      </Col>
      <Col>
        <Row align="middle" gutter={layout.spacerM}>
          {differencesCount > 0 && (
            <Col>
              <ActionPopconfirm isFilterEmpty={isFilterEmpty} onDelete={handleDeleteFilter}>
                <Button
                  onClick={!isFilterEmpty ? handleCreateFilter : undefined}
                  size="small"
                  type="link"
                >
                  {intl.formatMessage({
                    id: "savedFilters.status.saveAsNew",
                    defaultMessage: "Save as new",
                    description: "Saved filters save as new filter button",
                  })}
                </Button>
              </ActionPopconfirm>
            </Col>
          )}
          {differencesCount > 0 && (
            <Col>
              <ActionPopconfirm isFilterEmpty={isFilterEmpty} onDelete={handleDeleteFilter}>
                <Button
                  loading={isUpdateLoading}
                  onClick={!isFilterEmpty ? handleUpdateExistsFilter : undefined}
                  size="small"
                  type="primary"
                >
                  {intl.formatMessage({
                    id: "savedFilters.status.updateExistsFilter",
                    defaultMessage: "Update",
                    description: "Saved filters update exists filter button",
                  })}
                </Button>
              </ActionPopconfirm>
            </Col>
          )}
          <Col>
            <FontAwesomeIcon
              className={styles.clearFilterIcon}
              icon={faTimes}
              onClick={handleClearSelectedFilter}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  isUpdateLoading: isSavedFilterUpdateLoading(state),
});

const mapDispatchToProps = {
  updateSavedFilter: updateSavedFilter.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectedFilterState);
